'use client';

import { useAui } from '@assistant-ui/react';
import { startCase, truncate } from 'lodash';
import {
  BanIcon,
  CheckIcon,
  CircleCheckIcon,
  ClockIcon,
  ShieldQuestionIcon,
  type LucideIcon,
} from 'lucide-react';
import { useTranslations } from 'next-intl';

import { StatusAlert as StatusBanner } from '@/components/assistant-ui/approval/status-alert';
import { ToolDebugInfo } from '@/components/assistant-ui/tool-debug-info';
import type { ToolStatus } from '@/components/assistant-ui/tool-status';
import { useConfigValue } from '@/components/providers/config-provider';

export interface ApprovalPartState {
  readonly id: string;
  readonly approved?: boolean;
  readonly reason?: string;
  readonly isAutomatic?: boolean;
  readonly resolution?: 'cancelled' | 'expired';
}

export type ApprovalCardState =
  'pending' | 'approved' | 'auto-approved' | 'denied' | 'expired' | 'cancelled';

export function resolveApprovalCardState(approval: ApprovalPartState): ApprovalCardState {
  if (approval.resolution === 'expired') return 'expired';
  if (approval.resolution === 'cancelled') return 'cancelled';
  if (approval.approved === true) return approval.isAutomatic ? 'auto-approved' : 'approved';
  if (approval.approved === false) return 'denied';
  return 'pending';
}

/**
 * Builds the key→value rows shown on the approval card so the user can see what they're about to
 * approve (e.g. `Record Id: 123`). Includes only primitive fields — nested objects/arrays don't
 * render usefully in the compact card — caps at 8 rows, truncates values over 80 chars, and
 * title-cases the keys. `args` is `unknown` because tool call-arguments are arbitrary wire JSON.
 */
export function summarizeApprovalArgs(
  args: unknown,
): { key: string; label: string; value: string }[] {
  if (!args || typeof args !== 'object' || Array.isArray(args)) return [];
  return Object.entries(args as Record<string, unknown>)
    .filter(([, v]) => ['string', 'number', 'boolean'].includes(typeof v))
    .slice(0, 8)
    .map(([key, v]) => {
      const raw = String(v);
      return {
        key,
        label: startCase(key),
        value: truncate(raw, { length: 80, omission: '…' }),
      };
    });
}

export function humanizeToolName(toolName: string): string {
  return startCase(toolName);
}

function StatusAlert({
  tone,
  icon,
  children,
}: {
  tone: 'ok' | 'muted' | 'error';
  icon: LucideIcon;
  children: React.ReactNode;
}) {
  const cls =
    tone === 'ok'
      ? 'bg-primary/10 text-primary'
      : tone === 'error'
        ? 'bg-destructive/10 text-destructive'
        : 'bg-muted text-muted-foreground';
  return (
    <StatusBanner icon={icon} className={cls}>
      {children}
    </StatusBanner>
  );
}

export interface GenericApprovalCardViewProps {
  toolName: string;
  args: unknown;
  state: ApprovalCardState;
  reason?: string;
  onApprove?: () => void;
  onDecline?: () => void;
}

/** Pure presentation of the generic approval card — every state, no runtime hooks. */
export function GenericApprovalCardView({
  toolName,
  args,
  state,
  reason,
  onApprove,
  onDecline,
}: GenericApprovalCardViewProps) {
  const t = useTranslations('approvalCard');
  // Tenant-specific labels fall back to humanized identifiers.
  const labels = useTranslations(useConfigValue('approvalLabelsNamespace') ?? 'approvalCard');
  const rows = summarizeApprovalArgs(args);
  const toolLabel = labels.has(`tools.${toolName}`)
    ? labels(`tools.${toolName}`)
    : humanizeToolName(toolName);

  return (
    <div className="my-2 w-full max-w-md overflow-hidden rounded-xl border border-border bg-background shadow-sm">
      <div className="flex items-center gap-2.5 px-4 py-3">
        <span
          aria-hidden
          className="flex size-7 items-center justify-center rounded-full bg-primary/10 text-primary"
        >
          <ShieldQuestionIcon className="size-4" />
        </span>
        <div className="min-w-0">
          <h3 className="text-base leading-tight font-semibold text-foreground">{t('title')}</h3>
          <p className="truncate text-sm leading-tight text-muted-foreground">
            {t('toolRequest', { tool: toolLabel })}
          </p>
        </div>
      </div>

      {rows.length > 0 && (
        <>
          <div className="mx-4 h-px bg-border" />
          <dl className="grid grid-cols-2 gap-2 px-4 py-3">
            {rows.map(({ key, label, value }) => (
              <div key={key} className="rounded-lg border border-border bg-muted/40 px-2.5 py-1.5">
                <dt className="text-sm leading-tight text-muted-foreground">
                  {labels.has(`args.${key}`) ? labels(`args.${key}`) : label}
                </dt>
                <dd className="truncate text-sm leading-snug text-foreground" title={value}>
                  <bdi>{value}</bdi>
                </dd>
              </div>
            ))}
          </dl>
        </>
      )}

      <div className="mx-4 h-px bg-border" />

      <div className="flex min-h-[60px] items-center px-4 py-3" data-approval-state={state}>
        {state === 'pending' ? (
          <div className="flex w-full gap-2">
            <button
              type="button"
              onClick={onApprove}
              className="inline-flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              <CheckIcon className="size-4" aria-hidden />
              {t('approve')}
            </button>
            <button
              type="button"
              onClick={onDecline}
              className="inline-flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
            >
              <BanIcon className="size-4" aria-hidden />
              {t('decline')}
            </button>
          </div>
        ) : state === 'approved' ? (
          <StatusAlert tone="ok" icon={CircleCheckIcon}>
            {t('approved')}
          </StatusAlert>
        ) : state === 'auto-approved' ? (
          <StatusAlert tone="ok" icon={CircleCheckIcon}>
            {t('autoApproved')}
          </StatusAlert>
        ) : state === 'denied' ? (
          <StatusAlert tone="muted" icon={BanIcon}>
            {/* A reason is never something the guest typed here: it is a backend token, or the
                runtime's own English default for a decline given without one. Only the tokens
                with copy of their own say more than "declined". */}
            {reason === 'superseded'
              ? t('declinedSuperseded')
              : reason === 'expired'
                ? t('expired')
                : t('declined')}
          </StatusAlert>
        ) : state === 'cancelled' ? (
          <StatusAlert tone="muted" icon={BanIcon}>
            {t('cancelled')}
          </StatusAlert>
        ) : (
          <StatusAlert tone="muted" icon={ClockIcon}>
            {t('expired')}
          </StatusAlert>
        )}
      </div>
    </div>
  );
}

export interface GenericApprovalCardProps {
  toolName: string;
  toolCallId: string;
  args: unknown;
  approval: ApprovalPartState;
  status: ToolStatus;
  result?: unknown;
}

// Backend approval tools may override this card through a toolkit render entry.
export function GenericApprovalCard({
  toolName,
  toolCallId: _toolCallId,
  args,
  approval,
  status,
  result,
}: GenericApprovalCardProps) {
  const aui = useAui();
  const state = resolveApprovalCardState(approval);

  return (
    <ToolDebugInfo toolName={toolName} args={args} result={result} status={status}>
      <GenericApprovalCardView
        toolName={toolName}
        args={args}
        state={state}
        reason={approval.reason}
        onApprove={() => aui.part.respondToToolApproval({ approved: true })}
        onDecline={() => aui.part.respondToToolApproval({ approved: false })}
      />
    </ToolDebugInfo>
  );
}
